import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, Building2, Clock, User, CheckCircle, Cog, Image } from 'lucide-react';
import { LazyPhotoGallery } from '../../components/PhotoComponents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_OPTIONS = [
  { value: 'baja', label: 'Baja', color: 'slate' },
  { value: 'media', label: 'Media', color: 'amber' },
  { value: 'alta', label: 'Alta', color: 'orange' },
  { value: 'urgente', label: 'Urgente', color: 'rose' },
];

const STATUS_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente', color: 'amber' },
  { value: 'en_proceso', label: 'En Proceso', color: 'sky' },
  { value: 'resuelto', label: 'Resuelto', color: 'emerald' },
];

export default function AdminIncidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', resolution_notes: '' });

  const fetchIncidents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/incidents`);
      setIncidents(response.data);
    } catch (error) {
      toast.error('Error al cargar incidencias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleEdit = (incident) => {
    setSelectedIncident(incident);
    setUpdateData({ status: incident.status, resolution_notes: '' });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/incidents/${selectedIncident.id}`, updateData);
      toast.success('Incidencia actualizada');
      setEditDialogOpen(false);
      fetchIncidents();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta incidencia?')) return;
    try {
      await axios.delete(`${API}/incidents/${id}`);
      toast.success('Incidencia eliminada');
      fetchIncidents();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getPriorityBadge = (priority) => {
    const option = PRIORITY_OPTIONS.find(o => o.value === priority);
    const colors = {
      slate: 'text-slate-400',
      amber: 'text-amber-400',
      orange: 'text-orange-400',
      rose: 'text-rose-400',
    };
    return <span className={`font-mono text-xs uppercase ${colors[option?.color || 'slate']}`}>{option?.label || priority}</span>;
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    const colors = {
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      sky: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[option?.color || 'amber']}`}>
        {option?.label || status}
      </span>
    );
  };

  const pendingIncidents = incidents.filter(i => i.status === 'pendiente');
  const inProgressIncidents = incidents.filter(i => i.status === 'en_proceso');
  const resolvedIncidents = incidents.filter(i => i.status === 'resuelto');

  return (
    <Layout title="Gestión de Incidencias">
      <div className="space-y-6 animate-fade-in" data-testid="admin-incidents">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-amber-500/5 border-amber-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-rajdhani font-bold text-amber-400">{pendingIncidents.length}</p>
                <p className="text-xs text-amber-400/70 uppercase tracking-wider">Pendientes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-sky-500/5 border-sky-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-2xl font-rajdhani font-bold text-sky-400">{inProgressIncidents.length}</p>
                <p className="text-xs text-sky-400/70 uppercase tracking-wider">En Proceso</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-rajdhani font-bold text-emerald-400">{resolvedIncidents.length}</p>
                <p className="text-xs text-emerald-400/70 uppercase tracking-wider">Resueltas</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Incidents List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : incidents.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay incidencias registradas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {incidents.map((incident) => (
              <Card 
                key={incident.id} 
                className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all cursor-pointer ${
                  incident.priority === 'urgente' ? 'border-l-2 border-l-rose-500' : 
                  incident.priority === 'alta' ? 'border-l-2 border-l-orange-500' : ''
                }`}
                onClick={() => handleEdit(incident)}
                data-testid={`incident-card-${incident.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(incident.status)}
                        {getPriorityBadge(incident.priority)}
                        <span className="text-slate-500">•</span>
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {incident.company_name}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">{incident.title}</h3>
                      <p className="text-slate-400 mb-3">{incident.description}</p>
                      {/* Photos */}
                      {incident.photo_count > 0 && (
                        <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                          <LazyPhotoGallery id={incident.id} type="incidents" count={incident.photo_count} />
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        {incident.machine_name && (
                          <span className="flex items-center gap-1">
                            <Cog className="w-4 h-4" />
                            {incident.machine_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {incident.created_by_name}
                        </span>
                        <span className="flex items-center gap-1 font-mono">
                          <Clock className="w-4 h-4" />
                          {new Date(incident.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-rajdhani text-xl">Actualizar Incidencia</DialogTitle>
            </DialogHeader>
            {selectedIncident && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-slate-800/50 rounded-sm">
                  <h4 className="font-medium text-white mb-1">{selectedIncident.title}</h4>
                  <p className="text-sm text-slate-400">{selectedIncident.description}</p>
                  <p className="text-xs text-slate-500 mt-2">
                    Empresa: {selectedIncident.company_name} • Prioridad: {selectedIncident.priority}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Estado</Label>
                  <Select
                    value={updateData.status}
                    onValueChange={(v) => setUpdateData({ ...updateData, status: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="incident-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {updateData.status === 'resuelto' && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Notas de Resolución</Label>
                    <Textarea
                      value={updateData.resolution_notes}
                      onChange={(e) => setUpdateData({ ...updateData, resolution_notes: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
                      placeholder="Describe cómo se resolvió..."
                      data-testid="incident-resolution-input"
                    />
                  </div>
                )}
                <div className="flex justify-between pt-4">
                  <Button 
                    variant="ghost" 
                    onClick={() => handleDelete(selectedIncident.id)} 
                    className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                    data-testid="delete-incident-btn"
                  >
                    Eliminar
                  </Button>
                  <div className="flex gap-3">
                    <Button variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-400">
                      Cancelar
                    </Button>
                    <Button onClick={handleUpdate} className="bg-sky-600 hover:bg-sky-500" data-testid="update-incident-btn">
                      Actualizar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
